import axios, { type AxiosProgressEvent, type CancelTokenSource } from 'axios'
import { useCallback, useRef, useState } from 'react'

import { BACKEND_BASE_URL } from '@/config.ts'

export interface UploadType {
  file: File
  uploadId?: string
}

export type UploadProgress = {
  uploadId: string
  progress: number
  filename: string
}

const api = axios.create({
  baseURL: `${BACKEND_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'multipart/form-data',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

export const useFileAttachments = () => {
  const [uploadProgress, setUploadProgress] = useState<Record<string, UploadProgress>>({})
  const cancelTokensRef = useRef<Record<string, CancelTokenSource>>({})

  const cleanupUpload = useCallback((uploadId: string) => {
    setUploadProgress((prev) => {
      const { [uploadId]: removed, ...rest } = prev
      return rest
    })
    delete cancelTokensRef.current[uploadId]
  }, [])

  const upload = useCallback(
    async (file: File, uploadId: string): Promise<[string, Attachment]> => {
      const formData = new FormData()
      formData.append('file', file)

      const cancelToken = axios.CancelToken.source()
      cancelTokensRef.current[uploadId] = cancelToken

      setUploadProgress((prev) => ({
        ...prev,
        [uploadId]: { uploadId, progress: 0, filename: file.name },
      }))

      try {
        const { data: attachment } = await api.post<Attachment>('/attachments', formData, {
          cancelToken: cancelToken.token,
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              setUploadProgress((prev) => {
                return {
                  ...prev,
                  [uploadId]: {
                    ...prev[uploadId],
                    progress,
                  },
                }
              })
            }
          },
        })
        cleanupUpload(uploadId)
        return [uploadId, attachment]
      } catch (e) {
        cleanupUpload(uploadId)
        throw e
      }
    },
    [cleanupUpload],
  )

  const handleFilesUpload = useCallback(
    async (uploads: UploadType[]) => {
      const uploadPromises = uploads.map((u) => upload(u.file, u.uploadId ?? `${Date.now()}-${u.file.name}`))

      return Promise.all(uploadPromises)
    },
    [upload],
  )

  const cancelUpload = useCallback(
    (uploadId: string) => {
      const source = cancelTokensRef.current[uploadId]
      if (source) {
        source.cancel()
        cleanupUpload(uploadId)
      }
    },
    [cleanupUpload],
  )

  const download = useCallback(async (attachmentId: string) => {
    try {
      const response = await api.get(`/attachments/${attachmentId}`, {
        responseType: 'blob',
      })

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]))
      const contentDisposition = response.headers['content-disposition']
      let filename = 'download'
      if (contentDisposition) {
        const matches = /filename\*=UTF-8''(.+)/i.exec(contentDisposition)
        if (matches?.[1]) {
          filename = decodeURIComponent(matches[1])
        }
      }
      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : 'Download failed'
      console.error('Download failed:', errorMessage)
      throw error
    }
  }, [])

  return {
    uploadProgress,
    handleFilesUpload,
    cancelUpload,
    download,
    upload,
  }
}
